import { Offcanvas, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill-new";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import moment from "moment";

interface EventFormData {
  show: boolean;
  handleClose: () => void;
  data?: any;
}

type FormData = {
  year: string;
  name: string;
  designation: string;
  company: string;
  image: string;
};

const AddLeadershipCouncil = ({ show, handleClose, data }: EventFormData) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>();
  const [value, setValue] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    console.log(previewUrl);
    if (data) {
      reset({
        name: data.name,
        year: data.year,
        company: data.company,
        designation: data.designation,
        image: data.image,
      });
      setPreviewUrl(data?.originalImage || data?.image || null);
      setValue(data.description);
    } else {
      reset({});
      setPreviewUrl(null);
      setValue("");
    }
  }, [show]);
  const ALLOWED_IMAGE_TYPES = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/svg+xml",
    "image/svg",
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];

    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      const msg =
        "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

      toast.error(msg);
      setFile(null);
      setPreviewUrl("");
      input.value = "";
      return;
    }
    setFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };


  const onSubmit = (request: FormData) => {

    // ✅ Image validation
    if (!file && !previewUrl) {
      toast.error("Image is required");
      return;
    }
    const formData = new FormData();
    formData.append("name", request.name);
    formData.append("designation", request.designation);
    formData.append("company", request.company);
    formData.append("year", request.year);
    console.log("Submitting file:", file);
    if (file) {
      formData.append("image", file);
    }
    setLoading(true);
    if (data?._id) {
      WebService.fileUploadAPI({
        action: `leadership/editLeader/${data._id}`,
        body: formData,
      })
        .then((res: any) => {
          toast.success(res.message);
          setLoading(false);
          // if (file) {
          //     uploadBanner(res?.data?._id);
          // }
          handleClose();
          reset();
          setValue("");
          setFile(null);
        })
        .catch((error: any) => {
          setLoading(false);
          toast.error(error.response?.data?.message || "An error occurred");
        });
    } else {
      WebService.fileUploadAPI({
        action: `leadership/addLeader`,
        body: formData,
      })
        .then((res: any) => {
          toast.success(res.message);
          reset();
          handleClose();
          setFile(null);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
          toast.error(error.response?.data?.message || "An error occurred");
        });
    }
  };

  const uploadBanner = (id: string) => {
    const formData = new FormData();
    if (file) {
      formData.append("banner", file);
    }
    WebService.fileUploadAPI({
      action: `events/banner/${id}`,
      body: formData,
    })
      .then((res: any) => { })
      .catch((error: any) => {
        toast.error(error.response?.data?.message || "An error occurred");
      });
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={() => {
          reset();
          setFile(null);
          handleClose();
        }}
        placement="end"
        className="offcanvas-custom"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {data ? "Edit Leader" : "Add Leader"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Form>
            <Form.Group className="mb-2" controlId="eventImage">
              <Form.Label className="label">Image<span className="text-danger"> *</span></Form.Label>
              <Form.Control
                type="file"
                accept="image/.png,.jpg,.jpeg,.svg"
                className="custom-image-upload"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <div className="text-primary mt-1" style={{ fontSize: "13px" }}>
                Note: Please upload an image of size: <strong>180 × 128 px</strong> in <strong>.png, .jpeg, .jpg or .svg</strong> format.
              </div>
            </Form.Group>
            {previewUrl && (
              <div className="mb-2 event-image">
                <img
                  src={previewUrl}
                  alt="Image Preview"
                  className="w-auto max-h-32 object-contain mx-auto"
                />
              </div>
            )}
            <Form.Group className="mb-2" controlId="eventName">
              <Form.Label className="label">
                Name<span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                {...register("name", { required: "Name is required" })}
              />
              {errors.name && (
                <div className="error-message">{errors.name.message}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="eventLocation">
              <Form.Label className="label">
                Designation<span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter designation"
                {...register("designation", {
                  required: "Designation is required",
                })}
              />
              {errors.designation && (
                <div className="error-message">
                  {errors.designation.message}
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="eventCompany">
              <Form.Label className="label">
                Company<span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter company"
                {...register("company", { required: "Company is required" })}
              />
              {errors.company && (
                <div className="error-message">{errors.company.message}</div>
              )}
            </Form.Group>
            <Form.Group className="mb-2" controlId="eventYear">
              <Form.Label className="label">
                Year<span className="text-danger"> *</span>
              </Form.Label>
              <Form.Select
                {...register("year", { required: "Year is required" })}
                defaultValue=""
              >
                <option value="" disabled>
                  Select year
                </option>
                {Array.from({ length: 30 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </Form.Select>
              {errors.year && (
                <div className="error-message">{errors.year.message}</div>
              )}
            </Form.Group>
          </Form>
        </Offcanvas.Body>
        <div className="d-flex justify-content-end p-3">
          <CustomButton
            isLoading={isLoading}
            label="Save"
            className="w-100"
            onClick={handleSubmit(onSubmit)}
          />
        </div>
      </Offcanvas>
    </>
  );
};

export default AddLeadershipCouncil;
